import { useQuery } from '@apollo/client';
import classNames from 'classnames/bind';
import { useRouter } from 'next/router';
import { useContext, useEffect, useRef } from 'react';

import { IPromotion } from 'common/promotion';
import {
  IInternationalContextState,
  InternationalContext,
} from 'contexts/international';
import { Modal, ModalFuncContext, ModalPosition } from 'contexts/modalFunc';
import { SearchType } from 'contexts/searchParams';
import GET_MASKED_ID from 'detail/maskedId.gql';
import useAgreement from 'hooks/useAgreement';
import useAirportDetailList, {
  getUniqueLocationCodes,
} from 'hooks/useAirportDetailList';
import Agreement from 'list/common/agreement';
import {
  IExpressingInternationalList,
  IQueryValue,
} from 'list/international/types';
import DetailAgentSelect from './detailAgentSelect';
import DetailWrapper from './detailWrapper';
import styles from './index.module.scss';
import {
  getFavoriteParam,
  getParamSelectedFlight,
  getScheduleData,
} from './utils';

const cx = classNames.bind(styles);

interface IProps {
  loaded: boolean;
  promotions: IPromotion[];
  queryVariables: IQueryValue;
  selectedLabels: string[][];
  setCardFareType: React.Dispatch<React.SetStateAction<string | undefined>>;
  setMinFare: React.Dispatch<React.SetStateAction<number | undefined>>;
}

function Detail({
  loaded,
  promotions,
  queryVariables: {
    trip,
    itinerary,
    selectedFlight,
    fareType,
    adult: adultCnt,
    child: childCnt,
    infant: infantCnt,
    stayLength,
  },
  selectedLabels,
  setCardFareType,
  setMinFare,
}: IProps) {
  const { expFlights }: IInternationalContextState =
    useContext(InternationalContext);

  const {
    fares,
    schedules,
    fareTypes: korFareTypes,
    airlines,
  } = expFlights as IExpressingInternationalList;
  const router = useRouter();
  const { data: { airportDetailList } = { airportDetailList: [] } } =
    useAirportDetailList(getUniqueLocationCodes(itinerary));
  const airports = new Map(
    airportDetailList.map(airport => [airport.iataCode, airport]),
  );
  const selectedFlightList = getParamSelectedFlight(selectedFlight as string); // 1:스케쥴:카드요금조건/항공사,2:스케쥴:...
  const selectedScheduleIds = selectedFlightList.map(item => item.scheduleId);
  const selectedSchedulesData = getScheduleData(schedules, selectedScheduleIds);

  const { cardFareType: latestCardFareType } =
    selectedFlightList[selectedFlightList?.length - 1] || ''; // 마지막 카드 요금 조건
  const selectedCardFares = fares?.get(selectedScheduleIds.join('|')) || {}; // {A01:{}, A01/B05:{}, ...}
  const selectedFares =
    (selectedCardFares && selectedCardFares[latestCardFareType]) || []; // [{Adult:{}, Child:{}, ...}]

  const favoriteItem = selectedFares[0]
    ? getFavoriteParam({
        router,
        seatClass: fareType,
        korFareTypes,
        selectedFareItem: selectedFares[0],
        selectedSchedules: selectedSchedulesData,
        airlines,
        airports,
        passengerCount: { adultCnt, childCnt, infantCnt },
      })
    : null;
  const { data: maskedId } = useQuery(GET_MASKED_ID);
  const refetchRef = useRef(() => {});
  const selectedSchedules = selectedSchedulesData.map(({ detail }) => ({
    departureAirport: detail[0].sa,
    arrivalAirport: detail[detail.length - 1].ea,
    departureDate: detail[0].sdt.slice(0, 8),
  }));
  const {
    isAgreementRequired,
    isModalOn,
    ota,
    closeModal,
    getRedirectUrl,
    handleBooking,
  } = useAgreement(
    adultCnt,
    childCnt,
    infantCnt,
    fareType,
    selectedSchedules,
    SearchType.INTERNATIONAL,
    stayLength,
  );

  const setModal = useContext(ModalFuncContext);

  useEffect(() => {
    return () => {
      setModal({
        type: Modal.NULL,
        position: ModalPosition.DETAIL,
      });
    };
  }, []);

  useEffect(() => {
    setCardFareType(latestCardFareType);
  }, [latestCardFareType]);

  return (
    <DetailWrapper
      selectedFares={selectedFares}
      favoriteItem={favoriteItem}
      modal={
        isModalOn && (
          <Agreement
            isAgreementRequired={isAgreementRequired}
            naverId={maskedId?.maskedId}
            ota={ota}
            getRedirectUrl={getRedirectUrl}
            onClose={closeModal}
            refetch={refetchRef.current}
          />
        )
      }
    >
      <DetailAgentSelect
        itineraryList={itinerary}
        korFareTypes={korFareTypes}
        latestCardFareType={latestCardFareType}
        loaded={loaded}
        passengerCount={{ adultCnt, childCnt, infantCnt }}
        promotions={promotions}
        refetchRef={refetchRef}
        seatClass={fareType}
        selectedCardFares={selectedCardFares}
        selectedLabels={selectedLabels}
        selectedScheduleIds={selectedScheduleIds}
        selectedSchedulesData={selectedSchedulesData}
        trip={trip}
        handleBooking={handleBooking}
        setMinFare={setMinFare}
      />
    </DetailWrapper>
  );
}

export default function _DetailWrapper(props: IProps) {
  const { expFlights }: IInternationalContextState =
    useContext(InternationalContext);
  const { schedules } = expFlights || {};
  const selectedFlight = props.queryVariables.selectedFlight;
  const selectedFlightList = getParamSelectedFlight(selectedFlight as string);
  const selectedScheduleIds = selectedFlightList.map(item => item.scheduleId);
  const allLoadedSelectedSchedulesData = getScheduleData(
    schedules || [],
    selectedScheduleIds,
  ).every(schedule => !!schedule?.detail);

  if (
    !expFlights ||
    expFlights?.schedules?.filter(v => v)?.length !==
      selectedFlightList.length ||
    !allLoadedSelectedSchedulesData
  ) {
    return (
      <DetailWrapper>
        <div
          className={cx('list_loading_area')}
          aria-label="Loading..."
          aria-busy="true"
        />
      </DetailWrapper>
    );
  }

  return <Detail {...props} />;
}
